import React, { useRef, useState } from "react";
import JSZip from "jszip";
import { debuggerService } from "../../services/DebuggerService";
import { BenchmarkResults } from "../../types/debuggerServiceTypes";
import FlowchartIframe, { FlowchartIframeHandle } from "./FlowchartIframe";
import SingleBenchmarkCompare from "./SingleBenchmarkCompare";
import BatchBenchmarkTest from "./BatchBenchmarkTest";

interface BenchmarkControlsProps {
  onFilesLoaded?: (files: Record<string, string>) => void;
  onFilesExported?: (files: Record<string, string>) => void;
  onBenchmarkResults?: (results: BenchmarkResults) => void;
  children?: React.ReactNode; // For benchmark buttons
  numReviewedMessages?: number;
}

const BenchmarkControls: React.FC<BenchmarkControlsProps> = ({
  onFilesLoaded,
  onFilesExported,
  onBenchmarkResults,
  numReviewedMessages,
}) => {
  const [loadedFiles, setLoadedFiles] = useState<Record<string, string>>({});
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [prefix, setPrefix] = useState("benchmark/");
  const iframeRef = useRef<FlowchartIframeHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exportedFiles, setExportedFiles] = useState<Record<string, string>>(
    {}
  );

  const requestExport = (): Promise<Record<string, string>> =>
    new Promise((resolve) => {
      const listener = (e: MessageEvent) => {
        if (e.data?.type === "RETURN_FILES") {
          window.removeEventListener("message", listener);
          resolve(e.data.payload);
        }
      };
      window.addEventListener("message", listener);
      iframeRef.current?.postMessage({ type: "REQUEST_EXPORT" });
    });

  const handleFilesSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files) return;
    const newFiles: Record<string, string> = {};
    for (const f of Array.from(files)) {
      newFiles[f.name] = await f.text();
    }
    const merged = { ...loadedFiles, ...newFiles };
    setLoadedFiles(merged);
    onFilesLoaded?.(merged);
    e.target.value = "";

    // Send into iframe
    iframeRef.current?.postMessage({
      type: "INIT_FILES",
      payload: merged,
    });
    setStatus(`Loaded ${Object.keys(merged).length} files`);
  };

  const saveExported = async () => {
    setStatus("Generating ZIP…");
    try {
      const files = await requestExport();
      setExportedFiles(files);
      onFilesExported?.(files);
      if (!Object.keys(files).length) {
        setStatus(`ZIP failed: No Files Loaded to save`);
        return;
      }

      const zip = new JSZip();
      Object.entries(files).forEach(([name, content]) => {
        zip.file(name, content);
      });
      const now = new Date()
        .toLocaleString("en-US", {
          timeZone: "America/Los_Angeles",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .replace(/\D+/g, "_");
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Flowcharts_${now}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setStatus("ZIP downloaded");
    } catch (err: unknown) {
      console.error(err);
      setStatus(
        `ZIP failed: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    }
  };

  const handleUploadToServer = async () => {
    try {
      const files = await requestExport();
      setExportedFiles(files);
      onFilesExported?.(files);
      if (!Object.keys(files).length) {
        setStatus(`Upload to Server Failed: No Files Loaded to upload`);
        return;
      }
      await debuggerService.uploadBenchmarkDatabase({
        files,
        prefix,
      });
      setUploadStatus("Upload successful ✅");
    } catch (err: unknown) {
      console.error(err);
      setUploadStatus(
        `Upload failed: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    }
  };

  return (
    <div className="bg-white/90 rounded-lg shadow p-4">
      <div className="mb-4 bg-transparent">
        <h2 className="text-lg font-medium text-gray-900 mb-3 ">
          Flowchart Builder & Benchmark Testing
        </h2>
      </div>

      {/* Controls Row */}
      <div className="flex items-start gap-3 flex-wrap mb-4 bg-transparent">
        <label
          className="mt-1 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Select JSON files
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            multiple
            onChange={handleFilesSelected}
            className="hidden"
          />
        </label>

        <div className="flex flex-row gap-2 p-1 bg-[#46ACC6]/10 rounded-xl">
          <input
            type="text"
            placeholder="Prefix (e.g., benchmark/)"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base border border-[#46ACC6]/40 focus:border-[#46ACC6]/60 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#46ACC6]/20 bg-white/20 backdrop-blur-sm min-w-[120px]"
          />

          <button
            onClick={saveExported}
            disabled={Object.keys(loadedFiles).length === 0}
            className="mt-1 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 disabled:cursor-not-allowed border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Save Exported
          </button>
        </div>
        <button
          onClick={handleUploadToServer}
          disabled={Object.keys(loadedFiles).length === 0}
          className="mt-1 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 disabled:cursor-not-allowed border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>
          Upload to Server
        </button>

        <button
          onClick={() => iframeRef.current?.open()}
          className="mt-1 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Open Builder
        </button>

        {/* Single Benchmark Compare Component */}
        <SingleBenchmarkCompare prefix={prefix} />

        {/* Batch Benchmark Test Component */}
        <BatchBenchmarkTest
          prefix={prefix}
          onBenchmarkResults={onBenchmarkResults}
          numReviewedMessages={numReviewedMessages || 0}
        />
      </div>

      {/* Status messages - moved here to be right under the buttons */}
      <div className="space-y-2 mb-4 bg-transparent">
        {Object.keys(loadedFiles).length > 0 && (
          <div className="flex items-center text-sm text-gray-700 bg-transparent">
            <svg
              className="w-4 h-4 mr-2 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <strong>Loaded:</strong> {Object.keys(loadedFiles).join(", ")}
            </span>
          </div>
        )}

        {Object.keys(exportedFiles).length > 0 && (
          <div className="flex items-center text-sm text-gray-700 bg-transparent">
            <svg
              className="w-4 h-4 mr-2 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              <strong>Exported:</strong> {Object.keys(exportedFiles).join(", ")}
            </span>
          </div>
        )}

        {uploadStatus && (
          <div className="flex items-center text-sm bg-transparent">
            <svg
              className="w-4 h-4 mr-2 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">{uploadStatus}</span>
          </div>
        )}

        {status && (
          <div className="flex items-center text-sm text-gray-600 bg-transparent">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{status}</span>
          </div>
        )}
      </div>

      {/* Hidden-but-mounted iframe */}
      <FlowchartIframe
        ref={iframeRef}
        iframeSrc="https://avetalanguages.com/OnlineBuilder"
        onExport={() => {}}
        hideOpenButton
      />
    </div>
  );
};

export default BenchmarkControls;
